import { type BannerTargeting, findMatchingBanners } from '@skippay/core/modules/banners/filters';
import type { Banner } from '@skippay/core/modules/banners/types';

import { bannerSlugsToExclude } from 'utils/banners';

import { LIST_CAPS } from '../../constants';
import { ACCOUNT_BLOCKED_FLAG } from '../../masking';
import type { CustomerContext, OfferItem } from '../../types';
import type { DerivedSource } from '../types';

const toTargeting = (ctx: Readonly<CustomerContext>): BannerTargeting => ({
	hasMaxiPackage: ctx.account?.package ? ctx.account.package === 'maxi' : undefined,
	canApplyForRevolving: ctx.eligibility?.canApplyForRevolving,
	slugsToExclude: bannerSlugsToExclude({
		hasActiveDeferredPayment: (ctx.deferredPayments?.length ?? 0) > 0,
		hasActivePayInThree: (ctx.payInThree?.length ?? 0) > 0,
		hasPreferredBank: ctx.account?.hasPreferredBank,
	}),
});

const dedupeByPromoName = (banners: Banner[]): Banner[] => {
	const seen = new Set<string>();
	return banners.filter((banner) => {
		if (seen.has(banner.promoName)) return false;
		seen.add(banner.promoName);
		return true;
	});
};

const toOffer = (banner: Banner): OfferItem => ({
	id: banner.promoName,
	label: banner.botLabel || '',
	description: banner.botDescription || '',
	url: banner.bannerUrl || '',
});

export const bannersToOffers = (banners: Banner[], ctx: Readonly<CustomerContext>): OfferItem[] => {
	if (ctx.compliance?.actionsRequired?.includes(ACCOUNT_BLOCKED_FLAG)) return [];

	return dedupeByPromoName(findMatchingBanners(banners, 'overview', toTargeting(ctx)))
		.slice(0, LIST_CAPS.offers)
		.map(toOffer);
};

export const offersSource = (getBanners: () => Banner[]): DerivedSource => ({
	kind: 'derived',
	domains: ['offers'],
	toModel: (ctx) => (ctx.compliance ? { offers: bannersToOffers(getBanners(), ctx) } : {}),
});
